/**
 * Milk Flow: the calls, their shapes, their cache, and the words for each source and channel.
 *
 * Shapes read from `api/app/schemas/milk_flow.py` (14 October 2026; the day plan's arrivals and
 * projected start, the saved milk and the cream share on 15 October 2026). Litres only: there is no price
 * or amount in any of them (spec MF-4). Every figure carries its source or basis, and the words for
 * them are here, once, so the legend and the cells cannot disagree (MF-5). A source or channel the
 * page has no words for is shown by its own name rather than hidden (MF-3: a new one is a value).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { request } from "./client";

export type When = "past" | "today" | "future";

export interface MilkIn {
  source: string;
  basis: string;
  destination: string | null;
  location_name: string | null;
  litres: number;
  farms: number;
}

export interface MilkOut {
  channel: string;
  buyer_id: number | null;
  buyer_name: string | null;
  sales_contract_id: number | null;
  contract_number: string | null;
  location_name: string | null;
  /** "load" for a sale, "fill" for a cheese vat. */
  unit: string;
  units_planned: number | null;
  litres_planned: number | null;
  units_done: number;
  litres: number;
  units_without_litres: number;
  plan_notes: string | null;
  basis: string;
  /** Fills the PLC logged wrongly (0 L, too fast, no end); their litres are as logged. */
  units_flagged: number;
}

export interface Silo {
  silo: string;
  /** Null when the silo's stock is unknown; `unknown_reason` says why. */
  litres: number | null;
  basis: string;
  unknown_reason: string | null;
  last_event: string;
  as_of: string;
}

export interface MilkFlowSiloDay {
  silos: Silo[];
  /** Null unless every silo is known. */
  total_litres: number | null;
}

export interface MilkFlowWeekDay {
  day: string;
  when: When;
  milk_in: MilkIn[] | null;
  milk_out: MilkOut[] | null;
  silo: MilkFlowSiloDay | null;
  in_litres: number | null;
  in_forecast_litres: number | null;
  out_litres: number | null;
  /** The silos at the start of the day: the day before's end. Null unless every silo is known. */
  silo_start_litres: number | null;
  /** Null for a day the balance view has no row for (a day still to come). */
  balance: MilkFlowBalance | null;
  /** Gwalchmai from the plant's meter, the cheese plan and the collection rhythm (null when not readable). */
  plant?: PlantDay | null;
  /** The buyer's barrels: agreed against loaded. */
  barrels?: BarrelDay[] | null;
}

export interface PlantUnload {
  at: string;
  litres: number;
}

export interface PlantDay {
  day: string;
  start_litres: number | null;
  start_basis: "measured" | "projected";
  unloads: PlantUnload[];
  in_litres: number | null;
  /** expected: as on in_from, two days before, the same collection rhythm. */
  in_basis: "measured" | "so_far" | "so_far_and_expected" | "expected";
  in_from: string | null;
  in_so_far?: number | null;
  batches: number | null;
  batches_done?: number | null;
  batches_planned: number | null;
  cheese_litres: number | null;
  /** as_last_week: the batches the plant made on cheese_from, while no cheese plan is saved. */
  cheese_basis: "measured" | "planned" | "as_last_week";
  cheese_from: string | null;
  cheese_so_far?: number | null;
  plan_set_by: string | null;
  end_litres: number | null;
  end_basis: "measured" | "projected";
}

export interface BarrelDay {
  buyer_name: string | null;
  contract_number: string | null;
  loads_agreed: number | null;
  loads_loaded: number;
  litres: number;
  litres_per_load: number | null;
}

export type BalanceState = "balanced" | "incomplete" | "out_of_balance" | "in_progress";

/**
 * MF-9: milk in + on vehicles at the start + silos at the start = sold + into cheese + silos at the
 * end + on vehicles at the end + silo reset at cleaning + difference. `missing` names each gap of an
 * incomplete day, in words the database wrote; the page shows them as they are.
 */
export interface MilkFlowBalance {
  milk_in: number;
  on_board_start: number;
  silo_start: number | null;
  sold: number;
  cheese: number;
  silo_end: number | null;
  on_board_end: number;
  silo_reset_at_cleaning: number;
  difference: number;
  /** In litres: the 2% of the milk in that still counts as balanced. */
  tolerance: number;
  plc_unloaded: number;
  missing: string[];
  state: BalanceState;
}

export interface MilkFlowWeek {
  time_zone: string;
  week: string;
  today: string;
  /** Sections whose view could not be read: milk_in, milk_out, silo_stock. */
  unavailable: string[];
  days: MilkFlowWeekDay[];
  in_totals: { source: string; basis: string; destination: string | null; location_name: string | null; litres: number }[] | null;
  out_totals: {
    channel: string;
    buyer_name: string | null;
    contract_number: string | null;
    unit: string;
    units_planned: number | null;
    units_done: number;
    litres: number;
  }[] | null;
  /** The week's sums, from the API, so the page adds nothing up. */
  week_in_litres: number | null;
  week_in_forecast_litres: number | null;
  week_out_litres: number | null;
  silo_as_of: string | null;
  /** The newest PLC row read, local time without an offset: the stock has no day after it. */
  silo_data_through: string | null;
}

export interface FarmLine {
  producer_number: number;
  farm_name: string | null;
  source: string;
  basis: string;
  destination: string | null;
  location_name: string | null;
  litres: number;
  entries: number;
}

export interface SiloEvent {
  /** Null for an event the PLC logged with no silo. */
  silo: string | null;
  occurred_at: string;
  event: string;
  litres_in: number | null;
  litres_out: number | null;
  fill_litres_logged: number | null;
  litres_after: number | null;
  basis: string;
  unknown_reason: string | null;
  litres_at_cleaning?: number | null;
}

/** One moment of the day plan. `at` is UK local wall time without an offset; the end is the next
 *  day's 00:00. A level below 0 means the silos cannot fill the make: `short_litres` says by how much.
 *  An arrival is milk on board of a vehicle, expected an hour after its last pickup: a step up with
 *  the status "expected". The API sends the points in time order. */
export interface DayPlanPoint {
  at: string;
  kind: "start" | "unload" | "fill" | "cip" | "make" | "arrival" | "end";
  label: string;
  change: number | null;
  after: number | null;
  status: "done" | "planned" | "short" | "unknown" | "expected";
  short_litres: number | null;
}

export interface DayPlanMake {
  sequence: number;
  /** Local wall time without an offset. */
  starts: string;
  litres: number;
  status: "done" | "planned" | "short" | "missed";
  short_litres: number | null;
}

/** Milk on board of a vehicle for the plant, in the chart as an arrival. */
export interface DayPlanArrival {
  vehicle: string;
  /** UK local wall time without an offset: an hour after the last pickup, or now when that has passed. */
  at: string;
  litres: number;
  last_pickup_at: string;
}

/** Tony's day plan (MF-14): the silos from 00:00 to 24:00, what happened and what is planned. */
export interface DayPlan {
  start_litres: number | null;
  /** measured: the PLC's stock at the end of the day before; projected: the end this plan expects for it. */
  start_basis: "measured" | "projected";
  /** A sentence about a projected start, e.g. "Expected at the end of Wednesday 14 October". */
  start_note: string | null;
  end_litres: number | null;
  points: DayPlanPoint[];
  makes: DayPlanMake[];
  arrivals: DayPlanArrival[];
  has_plan: boolean;
  plan_set_by: string | null;
  plan_set_at: string | null;
  /** The milk on board for the plant, in the chart as the arrivals. */
  on_the_way_litres: number;
  /** Set while the transport planner writes no deliveries: milk not collected yet is not in the chart. */
  transport_plan_note: string | null;
}

export interface MilkFlowDay {
  time_zone: string;
  silo_data_through: string | null;
  day: string;
  when: When;
  today: string;
  unavailable: string[];
  farms: FarmLine[] | null;
  milk_out: MilkOut[] | null;
  silo: MilkFlowSiloDay | null;
  silo_events: SiloEvent[] | null;
  silo_start_litres: number | null;
  balance: MilkFlowBalance | null;
  /** Null when the silo events or the cheese plan could not be read. */
  day_plan: DayPlan | null;
}

/** The milk a day's batches are worked out from: predicted from each farm's latest lab results and
 *  the season's trend (a preview), measured once the day's milk is in, or saved: what a saved day was
 *  worked out from when it was saved. */
export interface CheesePlanMilk {
  fat_pct: number;
  protein_pct: number;
  basis: "predicted" | "measured" | "saved";
}

/**
 * A production day's limit from the production calendar (bsd-knowledge v0.141, PC-2): the lowest of its
 * active blocks. A block the system proposed counts already (PC-4); `planned` says whether a planned block
 * sets the limit (false: only a proposal waiting for a person does, SM-2). `block_ids` and `titles` are in the
 * same order, the lowest limit first.
 */
export interface CalendarLimit {
  batches_allowed: number;
  planned: boolean;
  block_ids: number[];
  reasons: BlockReason[];
  titles: string[];
}

/**
 * One day of the cheese plan. Production planning enters two numbers, the batches and the blocks per
 * batch (Ronald, 15 October 2026); the server works out the rest. For a day not planned (`batches`
 * null) the figures are a preview from last week's numbers, or null when there is none.
 */
export interface CheesePlanDay {
  day: string;
  /** Null: not planned yet. */
  batches: number | null;
  blocks_per_batch: number | null;
  set_by: string | null;
  set_at: string | null;
  note: string | null;
  previous_week: { batches: number; blocks_per_batch: number } | null;
  milk: CheesePlanMilk | null;
  /** Whole milk from the silos for the day. */
  litres: number | null;
  litres_per_batch: number | null;
  vat_fat_pct: number | null;
  vat_protein_pct: number | null;
  /** Null when the standomat is not used. */
  standomat_set_fat_pct: number | null;
  /** Sweet cream taken off by the standomat. */
  cream_kg: number | null;
  /** `HH:MM`, UK local. */
  batch_times: string[];
  /** For a saved day: the litres a batch needs with today's prediction, so the page can say when
   *  saving the day again would change the plan. */
  litres_per_batch_now: number | null;
  /** The production calendar's limit for the day, or null when nothing blocks it. */
  calendar: CalendarLimit | null;
}

/** The week's cheese target, from management's sales agreement. */
export interface CheeseTarget {
  cheese_kg: number;
  /** cheese_kg / block_kg. */
  blocks: number;
  set_by: string | null;
  set_at: string | null;
  note: string | null;
}

export interface CheesePlanWeek {
  week: string;
  today: string;
  time_zone: string;
  /** Whether this account may change the plan (production planning). */
  can_write: boolean;
  /** Whether this account may set the week's target (management, trading, admin). */
  can_set_target: boolean;
  unavailable: string[];
  block_kg: number;
  /** Which yield factors the milk was worked out with, e.g. "history-2026-10". */
  factor_set: string;
  /** What recent makes really gave against the model, e.g. 0.968. */
  yield_calibration: number | null;
  /** The share of the silo milk the plant took off as cream before the vats over the last 14 days
   *  (from the silo and vat fat in the make record), e.g. 0.0037. */
  recent_cream_share: number | null;
  target: CheeseTarget | null;
  /** The week's sums over the saved plan, from the API. */
  planned_blocks: number;
  planned_litres: number;
  planned_cream_kg: number;
  days: CheesePlanDay[];
}

export interface CheesePlanBody {
  day: string;
  batches: number;
  blocks_per_batch: number | null;
  note: string | null;
}

export interface CheeseTargetBody {
  /** A Monday. */
  week: string;
  cheese_kg: number;
  note: string | null;
}

export interface MilkFlowFarm {
  time_zone: string;
  producer_number: number;
  farm_name: string | null;
  date_from: string;
  date_to: string;
  today: string;
  unavailable: string[];
  days: { day: string; when: When; lines: FarmLine[] }[] | null;
}

// --- the silo timeline (16 October 2026) -------------------------------------------------------

/** start, end, unload, batch, top_up, cleaning (measured); arrival, planned_batch (expected). */
export type TimelineKind =
  | "start"
  | "end"
  | "unload"
  | "batch"
  | "top_up"
  | "cleaning"
  | "arrival"
  | "planned_batch";

export interface TimelinePoint {
  /** Local wall time without an offset. */
  at: string;
  kind: TimelineKind;
  change: number | null;
  after: number | null;
  basis: "measured" | "expected";
  /** short: a batch not made; it takes no milk, and the day's batches after it are not made either. */
  status: "done" | "planned" | "short" | "expected";
  short_litres?: number | null;
  /** A batch: the time the plan gave it. */
  planned_at?: string | null;
  /** An arrival: own or willis. A planned batch: plan or last_week. */
  source?: string | null;
  sequence?: number | null;
  farms?: string[] | null;
  /** A batch: its charge in blocks, 71 full, down to 60 (SC-3). */
  blocks?: number | null;
}

export interface TimelineDaySummary {
  start_litres: number | null;
  start_expected: boolean;
  in_litres: number;
  in_expected: boolean;
  tankers: number;
  batches: number;
  batch_litres: number;
  /** The blocks of each batch, in order: 71 a full charge, down to 60 for the last before a tanker (SC-3). */
  batch_blocks?: number[];
  batches_expected: boolean;
  short_batches: number;
  end_litres: number | null;
  end_expected: boolean;
}

export interface TimelineDay {
  day: string;
  calendar: CalendarLimit | null;
  /** The batches of the plan (or last week's) the calendar cuts: they take no milk. */
  batches_blocked: number;
  batches_planned: number | null;
  batches_source: string | null;
  /** The batches the milk covers, only where that differs from the plan. */
  batches_recommended: number | null;
  short_batches: number;
  /** False on a day after today in a week nothing is traded for yet: no silo figures (US-6 A, 21 October 2026). */
  silo_shown?: boolean;
  /** Milk to thermise (TH-2, spec 2026-10-27-thermise-early): the litres first seen on this day to pass L-5, else null. */
  thermise_litres?: number | null;
  /** Milk too old even thermised (TH-3): the litres past the thermised limit on this day, else null. */
  spot_sale_litres?: number | null;
  plan: TimelineDaySummary;
  recommended: TimelineDaySummary | null;
}

export interface TimelineFarm {
  farmer_id: number;
  farm_name: string;
  litres: number;
  own: boolean;
  /** Where its milk goes on a loading day, from its recent tickets. */
  destination: "barrel" | "gwalchmai";
}

export interface TimelineBarrels {
  day: string;
  /** Every traded barrel: contract, agreed, offered, tendered or proposed (US-4). */
  loads: number;
  agreed_litres: number;
  /** Contract or agreed; the rest is shown as expected. */
  firm_loads?: number;
  firm_litres?: number;
  loaded_loads: number;
  loaded_litres: number;
  willis_litres: number;
  to_barrels_litres: number;
  short_litres: number;
  to_gwalchmai_litres: number;
  farms: TimelineFarm[];
  measured: boolean;
}

export interface TimelineCollections {
  day: string;
  farms: TimelineFarm[];
  own_litres: number;
  willis_litres: number;
  to_barrels_litres: number;
  to_gwalchmai_litres: number;
}

/** A whole week to come: the forecast's milk against the cheese plan and the agreed barrels. */
export interface TimelineWeek {
  week: string;
  supply_litres: number;
  cheese_litres: number;
  barrels_litres: number;
  /** Positive: milk to sell; negative: milk to buy. Never both. */
  balance_litres: number;
  /** Every day has a cheese plan and a forecast. */
  complete: boolean;
  /** The week today is in. */
  current: boolean;
  /** The litres of the batches the production calendar blocks: milk to sell (AP-2). */
  blocked_litres: number;
  /** A block was entered after the Wednesday proposal for this week: that milk is surplus to place. */
  blocked_after_proposal: boolean;
}

/** A risk of the next 48 hours (AP-4), with the people who act on it. */
export interface TimelineAlert {
  kind:
    | "batch_short"
    | "barrels_short"
    | "tanker_late"
    | "documents_missing"
    | "calendar"
    | "not_placed"
    | "spot_sale"
    | "thermise";
  role: string;
  role_code: string | null;
  people: string[];
  day: string;
  at: string | null;
  sequence: number | null;
  litres: number | null;
  loads: number | null;
  source: string | null;
  /** calendar: the cheese plan's batches on a day the calendar limits to fewer. */
  batches_planned?: number | null;
  batches_allowed?: number | null;
  block_ids?: number[] | null;
  /** thermise: the day the milk came in, and the day it passes L-5, the last day to thermise it. */
  came_in?: string | null;
  by?: string | null;
}

/** One buyer's barrels on a day: the litres the day's J.H. Willis farms fill of them. */
export interface TradeBuyer {
  buyer: string | null;
  reserved_as: "contract" | "agreed" | "offered" | "tendered" | "proposed";
  firm: boolean;
  loads: number;
  litres: number;
  to_barrels: number;
}

/**
 * One day of the milk the farms give and where it goes (US-7 A, 21 October 2026): Gwalchmai as the factory ordered
 * it, each buyer's barrels and what is still to trade add up to the intake. The source is our producers until milk
 * bought from others is recorded (US-10 A).
 */
export interface TradeDay {
  day: string;
  intake_litres: number;
  sources: { source: "producers"; litres: number }[];
  gwalchmai_litres: number;
  buyers: TradeBuyer[];
  barrels_short_litres: number;
  still_to_trade_litres: number;
  week_traded: boolean;
}

/** A batch the production calendar blocks: a grey mark on the line, with no step. */
export interface TimelineBlockedBatch {
  day: string;
  sequence: number;
  at: string;
  litres: number;
  block_ids: number[];
}

export interface TimelineBatch {
  day: string;
  sequence: number;
  at: string;
  planned_at: string;
  litres: number;
  status: "planned" | "short" | "missed";
  short_litres: number | null;
  source: string;
}

export interface Timeline {
  time_zone: string;
  first: string;
  days: number;
  today: string;
  unavailable: string[];
  data_through: string | null;
  start_known: boolean;
  /** The last day of the one plan: no window goes past it (SC-1). */
  horizon?: string | null;
  /** A full charge in blocks (SC-3). */
  full_charge_blocks?: number | null;
  points: TimelinePoint[];
  recommended_points: TimelinePoint[] | null;
  batches: TimelineBatch[];
  blocked_batches: TimelineBlockedBatch[];
  day_rows: TimelineDay[];
  barrels: TimelineBarrels[];
  collections: TimelineCollections[];
  weeks: TimelineWeek[];
  alerts: TimelineAlert[];
  trade_days?: TradeDay[];
  unload_times: Record<string, string[]>;
}

/** One vehicle's day in the plan in operation (16 October 2026). */
export interface PlanVehicle {
  vehicle: "own" | "willis";
  /** On its tickets of the last 7 days, most used first. */
  plates: string[];
  farms: TimelineFarm[];
  litres: number;
  to_barrels_litres: number;
  to_gwalchmai_litres: number;
  /** HH:MM, one per tanker to Gwalchmai. */
  at_gwalchmai: string[];
}

export interface PlanDay {
  day: string;
  collection_day: boolean;
  barrels: { loads: number; agreed_litres: number; to_barrels_litres: number; short_litres: number } | null;
  vehicles: PlanVehicle[];
}

/** The collections as they are driven now, per day and vehicle, from the farms' own tickets. */
export interface OperatingPlan {
  time_zone: string;
  first: string;
  days: number;
  today: string;
  unavailable: string[];
  days_plan: PlanDay[];
  unload_times: Record<string, string[]>;
}

// --- the production calendar (bsd-knowledge v0.141, spec 2026-10-18-production-calendar) -------------

export type BlockReason = "maintenance" | "project" | "cleaning" | "staffing" | "other";
export const BLOCK_REASONS: BlockReason[] = ["maintenance", "project", "cleaning", "staffing", "other"];
export const REASON_WORDS: Record<BlockReason, string> = {
  maintenance: "Maintenance",
  project: "Project",
  cleaning: "Cleaning",
  staffing: "Staffing",
  other: "Other",
};

/** An objection to a block by anyone who reads the calendar: no veto, shown beside it (SM-4). */
export interface BlockObjection {
  id: number;
  raised_by: string | null;
  raised_at: string;
  objection: string;
}

/**
 * One planned stop (PC-2). Its days, batches, reason and title never change: a change replaces it. A person's
 * block is planned at once (SM-1); only a block the system proposes waits as proposed (SM-2).
 */
export interface ProductionBlock {
  id: number;
  first_day: string;
  last_day: string;
  batches_allowed: number;
  reason: BlockReason;
  title: string;
  note: string | null;
  eam_service_order: string | null;
  eam_project_code: string | null;
  source: "manual" | "eam" | "people";
  status: "proposed" | "planned" | "cancelled";
  /** None for a block the system proposed. */
  entered_by: string | null;
  entered_at: string;
  /** Who took over a proposal. */
  accepted_by: string | null;
  accepted_at: string | null;
  cancelled_by: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  replaces_id: number | null;
  replaced_by_id: number | null;
  objections: BlockObjection[];
  can_accept: boolean;
  can_cancel: boolean;
}

export interface ProductionCalendarDay {
  day: string;
  calendar: CalendarLimit | null;
  /** A mark only: it blocks nothing (PC-10). */
  bank_holiday: string | null;
}

export interface ProductionCalendar {
  first: string;
  last: string;
  today: string;
  time_zone: string;
  unavailable: string[];
  /** Enter, change, withdraw, accept and reject: one role (SM-3). */
  can_enter: boolean;
  /** HH:MM: the usual draw of batch 1, 2 and 3. */
  draw_times: string[];
  /** The days of the range with a block or a bank holiday. */
  days: ProductionCalendarDay[];
  blocks: ProductionBlock[];
}

export interface ProductionBlockBody {
  first_day: string;
  last_day: string;
  batches_allowed: number;
  reason: BlockReason;
  title: string;
  note: string | null;
  eam_service_order: string | null;
  eam_project_code: string | null;
  replaces: number | null;
}

/** The block in the form, as far as its consequence goes. */
export interface BlockConsequenceBody {
  first_day: string;
  last_day: string;
  batches_allowed: number;
  replaces: number | null;
}

/** What a block would do, before it is saved (SM-4): counted as Milk Flow counts it. */
export interface BlockConsequence {
  first_day: string;
  last_day: string;
  batches_allowed: number;
  batches_lost: number;
  litres_freed: number;
  days: {
    day: string;
    /** plan: counted on the one plan; plant: past its horizon, the plant's batches at a full charge. */
    source: string;
    batches_now: number;
    batches_then: number;
    batches_lost: number;
    litres_freed: number;
  }[];
  weeks: {
    week: string;
    /** Positive is milk to sell, negative milk to buy. */
    balance_now_litres: number;
    balance_then_litres: number;
    change_litres: number;
    complete: boolean;
    proposal_at: string;
    proposal_gone: boolean;
  }[];
  unavailable: string[];
}

/** A line on To do (SM-4): a block entered lately, or a proposal waiting for a person. */
export interface NewBlockLine {
  block: ProductionBlock;
  /** Who holds the roles it concerns today. */
  people: string[];
  role_codes: string[];
}

export interface NewBlocks {
  unavailable: string[];
  can_enter: boolean;
  lines: NewBlockLine[];
}

export const milkFlowApi = {
  /** From `first` (today when absent) for `days` days. */
  timeline: (first?: string, days?: number) =>
    request<Timeline>("/api/milk-flow/timeline", { from: first, days }),
  alerts: () => request<Timeline>("/api/milk-flow/alerts"),
  /** The plan in operation from `first` (today when absent). */
  collections: (first?: string, days?: number) =>
    request<OperatingPlan>("/api/milk-flow/collections", { from: first, days }),
  week: (monday?: string) => request<MilkFlowWeek>("/api/milk-flow/week", { week: monday }),
  day: (day: string) => request<MilkFlowDay>(`/api/milk-flow/days/${encodeURIComponent(day)}`),
  farm: (producer: number, weeks: number) =>
    request<MilkFlowFarm>(`/api/milk-flow/farms/${producer}`, { weeks }),
  /** A Monday; the API answers next week when absent. */
  cheesePlan: (monday?: string) => request<CheesePlanWeek>("/api/milk-flow/cheese-plan", { week: monday }),
  /** One day's makes, as a new plan for that day. 204: nothing comes back. */
  setCheesePlan: (body: CheesePlanBody) =>
    request<void>("/api/milk-flow/cheese-plan", {}, { method: "POST", body }),
  /** The week's target in kg of cheese. 204: nothing comes back. */
  setCheeseTarget: (body: CheeseTargetBody) =>
    request<void>("/api/milk-flow/cheese-target", {}, { method: "POST", body }),
  /** From `first` (today when absent) to `last` (eight weeks on when absent). */
  productionCalendar: (first?: string, last?: string) =>
    request<ProductionCalendar>("/api/milk-flow/production-calendar", { from: first, to: last }),
  /** What the block in the form would do; nothing is saved. */
  blockConsequence: (body: BlockConsequenceBody) =>
    request<BlockConsequence>("/api/milk-flow/production-calendar/consequence", {}, { method: "POST", body }),
  enterBlock: (body: ProductionBlockBody) =>
    request<{ id: number; status: string }>("/api/milk-flow/production-calendar", {}, { method: "POST", body }),
  acceptBlock: (id: number) =>
    request<void>(`/api/milk-flow/production-calendar/${id}/accept`, {}, { method: "POST", body: {} }),
  rejectBlock: (id: number, cancel_reason: string) =>
    request<void>(`/api/milk-flow/production-calendar/${id}/reject`, {}, { method: "POST", body: { cancel_reason } }),
  objectToBlock: (id: number, objection: string) =>
    request<{ id: number }>(`/api/milk-flow/production-calendar/${id}/objection`, {}, {
      method: "POST",
      body: { objection },
    }),
  newBlocks: () => request<NewBlocks>("/api/milk-flow/production-calendar/new"),
  cancelBlock: (id: number, cancel_reason: string) =>
    request<void>(`/api/milk-flow/production-calendar/${id}/cancel`, {}, { method: "POST", body: { cancel_reason } }),
};

export function useMilkFlowWeek(monday: string | undefined) {
  return useQuery({
    queryKey: ["milk-flow", "week", monday ?? "current"],
    queryFn: () => milkFlowApi.week(monday),
  });
}

export function useMilkFlowTimeline(first: string | undefined, days?: number, enabled = true) {
  return useQuery({
    queryKey: ["milk-flow", "timeline", first ?? "current", days ?? "default"],
    queryFn: () => milkFlowApi.timeline(first, days),
    enabled,
  });
}

/** The alerts of the next 48 hours, for To do and Plan; refreshed every five minutes like the plant's feed. */
export function useMilkFlowAlerts(enabled = true) {
  return useQuery({
    queryKey: ["milk-flow", "alerts"],
    queryFn: () => milkFlowApi.alerts(),
    enabled,
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useOperatingPlan(first: string | undefined, days?: number, enabled = true) {
  return useQuery({
    queryKey: ["milk-flow", "collections", first ?? "today", days ?? "default"],
    queryFn: () => milkFlowApi.collections(first, days),
    enabled,
  });
}

export function useMilkFlowDay(day: string, enabled = true) {
  return useQuery({ queryKey: ["milk-flow", "day", day], queryFn: () => milkFlowApi.day(day), enabled });
}

export function useMilkFlowFarm(producer: number, weeks: number, enabled = true) {
  return useQuery({
    queryKey: ["milk-flow", "farm", producer, weeks],
    queryFn: () => milkFlowApi.farm(producer, weeks),
    enabled,
  });
}

export function useProductionCalendar(first?: string, last?: string) {
  return useQuery({
    queryKey: ["milk-flow", "production-calendar", first ?? "today", last ?? "ahead"],
    queryFn: () => milkFlowApi.productionCalendar(first, last),
  });
}

/** A block changes what every Milk Flow page shows: the cap, the line, the alerts and the weeks. */
function useCalendarWrite<T, R>(write: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ["milk-flow"] });
    },
  });
}

export const useEnterBlock = () => useCalendarWrite((body: ProductionBlockBody) => milkFlowApi.enterBlock(body));
export const useAcceptBlock = () => useCalendarWrite((id: number) => milkFlowApi.acceptBlock(id));
export const useRejectBlock = () =>
  useCalendarWrite(({ id, reason }: { id: number; reason: string }) => milkFlowApi.rejectBlock(id, reason));
export const useCancelBlock = () =>
  useCalendarWrite(({ id, reason }: { id: number; reason: string }) => milkFlowApi.cancelBlock(id, reason));
export const useObjectToBlock = () =>
  useCalendarWrite(({ id, objection }: { id: number; objection: string }) => milkFlowApi.objectToBlock(id, objection));

/** What the block in the form would do (SM-4): asked again whenever the form's block changes. */
export function useBlockConsequence(body: BlockConsequenceBody | null) {
  return useQuery({
    queryKey: ["milk-flow", "block-consequence", body],
    queryFn: () => milkFlowApi.blockConsequence(body as BlockConsequenceBody),
    enabled: body !== null,
    retry: false,
  });
}

/** For To do (SM-4): the blocks entered lately and the proposals waiting for a person. */
export function useNewBlocks() {
  return useQuery({
    queryKey: ["milk-flow", "production-calendar", "new"],
    queryFn: () => milkFlowApi.newBlocks(),
    retry: false,
  });
}

/** Each refusal of a block, in plain words, for a refusal without the API's own sentence. */
export const BLOCK_REFUSALS: Record<string, string> = {
  DAY_IN_THE_PAST: "A block is a plan for days to come.",
  TOO_FAR: "A block starts at most 8 weeks ahead.",
  BAD_RANGE: "The last day is before the first day.",
  RANGE_TOO_LONG: "A block covers at most 92 days.",
  BAD_BATCHES: "A block leaves no batch, 1 or 2.",
  BAD_REASON: "Choose a reason.",
  BAD_TITLE: "Say what it is, in 3 to 120 characters.",
  NOTE_REQUIRED: "Say in the note what the reason is.",
  NO_NOTE_ON_STAFFING: "A staffing block has no note.",
  BAD_EAM_REFERENCE: "An EAM work order reads WO-2026-0085; a project 2026_0006 or K2017082.",
  NOT_A_PROPOSAL: "This block is planned already: cancel it instead.",
  ALREADY_CANCELLED: "This block is cancelled already.",
  CANCEL_REASON_REQUIRED: "Say in a few words why.",
  BAD_OBJECTION: "Say the objection in 3 to 500 characters.",
  BLOCK_REFUSED: "The database refused the block.",
  ROLE_NOT_PERMITTED: "Only production planning can enter a block.",
  USER_NOT_PROVISIONED: "Only production planning can enter a block.",
};

/** Each refusal of a plan, in plain words, next to the day it is about. A 422 shows the API's own
 *  sentence instead (it can say, for instance, how many litres a batch of that many blocks needs);
 *  these are the words before anything is sent, and for a refusal without a sentence. */
export const PLAN_REFUSALS: Record<string, string> = {
  BAD_BATCHES: "A day has 0 to 4 batches.",
  BAD_BLOCKS: "A batch is 1 to 120 blocks.",
  NO_MILK_COMPOSITION:
    "The milk cannot be worked out: there are no recent fat and protein figures to work it out from.",
  PLAN_IN_THE_PAST: "This day has passed, so its plan cannot be changed.",
  PLAN_TOO_FAR: "This day is too far ahead to plan yet.",
  PLAN_REFUSED: "The plan for this day was not saved: the database refused it.",
  DAY_BLOCKED: "The production calendar allows fewer batches on this day.",
  CALENDAR_NOT_READ: "The production calendar could not be read, so the plan was not saved.",
  ROLE_NOT_PERMITTED: "Only production planning can change the plan.",
  USER_NOT_PROVISIONED: "Only production planning can change the plan.",
};

/** Each refusal of a target, in plain words, next to the target form. */
export const TARGET_REFUSALS: Record<string, string> = {
  BAD_TARGET: "The target is a number of kilograms of cheese above 0.",
  WEEK_NOT_MONDAY: "A target is for a week that starts on a Monday.",
  ROLE_NOT_PERMITTED: "Only management and trading can set the target.",
  USER_NOT_PROVISIONED: "Only management and trading can set the target.",
};

export const BALANCE_WORDS: Record<BalanceState, string> = {
  balanced: "Balanced",
  incomplete: "Incomplete",
  out_of_balance: "Out of balance",
  in_progress: "Today",
};

// --- words ---------------------------------------------------------------------------------------

const SOURCES: Record<string, string> = {
  gardner_denver_optiload: "Vehicle meter (Optiload)",
  gea_diessel_zevodat_flash: "Vehicle meter (GEA)",
  intake_workbook: "Intake workbook",
  forecast: "Forecast",
};

/** What each source is, and what is uncertain about it: the legend under each table (MF-5). */
export const SOURCE_NOTES: Record<string, string> = {
  gardner_denver_optiload: "Measured at the farm by the Willis tanker's meter. The farmer is paid on this.",
  gea_diessel_zevodat_flash: "Measured at the farm by the meter on the Burg. The farmer is paid on this.",
  intake_workbook: "From the intake workbook, only where there is no meter figure.",
  forecast: "Expected, not yet collected.",
};

export const CHANNEL_NOTES: Record<string, string> = {
  sold: "Loaded into the buyer's tanker, from the delivery note. Future days show the agreed loads.",
  cheese: "Into the cheese vats, as the plant measured it.",
};

export const SILO_NOTE =
  "What was unloaded minus what went into the vats, counted from each silo's last cleaning. Shows unknown after a faulty reading, until the next cleaning.";

export function sourceLabel(source: string): string {
  return SOURCES[source] ?? source;
}

export function destinationLabel(destination: string | null, location: string | null): string {
  if (destination === "both") return "Plant and a buyer's trailer";
  if (destination === "none") return "Destination not recorded";
  return location ?? (destination === "plant" ? "Plant" : destination === "buyer_trailer" ? "Buyer's trailer" : "—");
}

export function channelLabel(channel: string, buyer: string | null): string {
  if (channel === "cheese") return "Cheese";
  if (channel === "sold") return buyer ? `Sold to ${buyer}` : "Sold";
  return buyer ? `${channel}: ${buyer}` : channel;
}

const REASONS: Record<string, string> = {
  no_cip_yet: "no cleaning recorded yet to count from",
  fill_zero_litres: "a vat fill logged 0 L",
  fill_too_fast: "a vat fill logged more litres than the line can pump",
  fill_without_end: "a vat fill logged no end, so no litres",
  split_with_unknown_silo: "a vat fill drew from this silo and an unknown one",
};

const SHORT_REASONS: Record<string, string> = {
  no_cip_yet: "no cleaning yet",
  fill_zero_litres: "a fill logged 0 L",
  fill_too_fast: "a fill logged too fast",
  fill_without_end: "a fill without an end",
  split_with_unknown_silo: "a fill shared with an unknown silo",
};

/** For a table cell: `Unknown: a fill without an end`. */
export function unknownShort(reason: string | null): string {
  return reason ? `Unknown: ${SHORT_REASONS[reason] ?? reason}` : "Unknown";
}

export function unknownReason(reason: string | null): string {
  if (!reason) return "unknown";
  return `unknown: ${REASONS[reason] ?? reason}, until the silo's next cleaning`;
}

export const SECTION_NAMES: Record<string, string> = {
  milk_in: "Milk in",
  milk_out: "Milk out",
  silo_stock: "Silo stock",
  farms: "Farms",
  silo_events: "Silo events",
  farm_days: "This farm's days",
  balance: "Balance",
  cheese_plan: "Cheese plan",
  on_board: "Milk on board",
  inputs: "What the plan is worked out from",
  barrel_min: "The least a barrel holds",
  target: "The week's target",
  plc_events: "The plant's meter",
  collections: "The farms' collections",
  unloads: "The plant's unloads",
  own_plates: "Our own vehicles",
  forecast: "The farms' forecast",
  sold: "The barrels sold",
  calendar: "The production calendar",
  blocks: "The blocks",
  bank_holidays: "Bank holidays",
  draw_times: "The usual draw times",
};
